/**
 * Cycle Report Store — Brief G
 *
 * Manages persisted cycle reports (Plan of Record read-models) per Doc 07 §6.
 *
 * Lifecycle:
 *  1. writeCycleReport()   — called atomically from acceptProposal() (Brief C)
 *  2. publishCycleReport() — optional step that transitions ratified → published
 *
 * Versioning:
 *  - Each new ratification produces a new row with version N+1.
 *  - The prior row is marked legitimacy_state = 'superseded'.
 *  - Reports are immutable after creation; overrides produce new versioned rows.
 */

import { create } from 'zustand';
import { getSafeSupabaseClient, isSupabaseAvailable } from '../../lib/supabaseSync';
import { sendNotification } from '../../lib/notificationService';
import { dispatchPrioritizationDashboardRefresh } from '../../lib/prioritizationDashboardRefreshEvents';

// ============================================================================
// Types
// ============================================================================

export type LegitimacyState = 'ratified' | 'published' | 'superseded';

/** Canonical shape for plan_of_record jsonb column (Brief G §2). */
export interface PlanOfRecord {
  ranked_items: Array<{
    rank: number;
    item_id: string;
    title: string;
    track_cell: {
      category: string;
      altitude: number;
    };
    above_cut_line: boolean;
    key_attributes: Record<string, unknown>;
    override_applied: boolean;
  }>;
  cut_line_position: number | null;
  total_items_considered: number;
  loop_count: number;
  session_count: number;
  value_collection_snapshot_id: string | null;
  assembled_at: string;
}

/** Scoring basis — criteria and weights that produced the accepted ranking. */
export interface ScoringBasis {
  criteria: Array<{ key: string; label: string; weight: number | null }>;
  value_snapshot_id: string | null;
  scoring_method: string;
  assembled_at: string;
}

/** One entry in the amendment log — a diff between two versions of the PoR. */
export interface AmendmentLogEntry {
  type: 'added' | 'removed' | 'rank_change';
  item_id: string;
  item_title: string;
  previous_rank: number | null;
  new_rank: number | null;
  justification: string | null;
  authorized_by_id: string | null;
  authorized_at: string | null;
}

/** Full cycle report row (maps 1:1 to the DB table after migrations 315, 478). */
export interface CycleReport {
  id: string;
  cycleId: string;
  cycleName?: string;
  cycleMode?: string;
  cycleOwnerId?: string;
  effectiveDate?: string;
  timeHorizon?: string;
  prioritySetId?: string;
  loopReportId?: string;
  loopResultIds: string[];
  proposalId?: string;
  rankedItemIds: string[];
  cutLinePosition?: number;
  itemSnapshot: Array<{ rank: number; item_id: string; title: string; attributes: Record<string, unknown> }>;
  legitimacyState: LegitimacyState;
  planOfRecord: PlanOfRecord;
  scoringBasis?: ScoringBasis;
  ratificationAuthority?: string;
  amendmentLog?: AmendmentLogEntry[];
  overrides?: unknown;
  rationale?: string;
  assumptions?: string;
  risks?: string;
  decisionNote?: string;
  decisionConfidence?: string;
  governanceEventIds: string[];
  publicationMessage?: string;
  publicationUrl?: string;
  notifiedStakeholderIds: string[];
  version: number;
  supersedesId?: string;
  ratifiedAt?: string;
  ratifiedBy?: string;
  publishedAt?: string;
  publishedBy?: string;
  createdBy: string;
  createdAt: string;
  workspaceId?: string;
}

/** Summary row returned by fetchCycleReportHistory. */
export interface CycleReportHistoryRow {
  id: string;
  version: number;
  legitimacyState: LegitimacyState;
  ratifiedAt?: string;
  publishedAt?: string;
  createdBy: string;
  rationale?: string;
  amendmentLog?: AmendmentLogEntry[];
}

interface CycleReportStoreState {
  reports: CycleReport[];
  isLoading: boolean;
  error: string | null;

  // Write paths
  writeCycleReport: (params: {
    cycleId: string;
    loopId: string;
    proposalId: string;
    actorId: string;
    workspaceId: string;
  }) => Promise<string | null>;

  publishCycleReport: (params: {
    reportId: string;
    publicationMessage: string;
    publicationUrl?: string;
    stakeholderIds?: string[];
    actorId: string;
    workspaceId: string;
  }) => Promise<{ success: boolean; error: string | null }>;

  // Read paths
  fetchCurrentCycleReport: (cycleId: string) => Promise<CycleReport | null>;
  fetchCycleReportHistory: (cycleId: string) => Promise<CycleReportHistoryRow[]>;
  fetchReportsForCycle: (cycleId: string) => Promise<void>;

  // Local selectors
  getLatestReportForCycle: (cycleId: string) => CycleReport | undefined;
  getReportsForCycle: (cycleId: string) => CycleReport[];
  getPublishedReport: (cycleId: string) => CycleReport | undefined;
}

// ============================================================================
// Row mapper
// ============================================================================

/** Session facilitators + participants for a cycle (publish notify defaults). */
async function resolveCyclePublishRecipients(
  supabase: NonNullable<ReturnType<typeof getSafeSupabaseClient>>,
  cycleId: string
): Promise<string[]> {
  const { data: sessions, error } = await supabase
    .from('prioritization_sessions')
    .select('id, facilitator_id')
    .eq('cycle_id', cycleId);
  if (error) {
    console.warn('[CycleReport] resolveCyclePublishRecipients sessions:', error.message);
    return [];
  }
  const ids = new Set<string>();
  const sessionIds: string[] = [];
  for (const s of sessions ?? []) {
    if (s.facilitator_id) ids.add(s.facilitator_id as string);
    if (s.id) sessionIds.push(s.id as string);
  }
  if (sessionIds.length > 0) {
    const { data: parts, error: pErr } = await supabase
      .from('session_participants')
      .select('user_id')
      .in('session_id', sessionIds);
    if (pErr) {
      console.warn('[CycleReport] resolveCyclePublishRecipients participants:', pErr.message);
    } else {
      for (const p of parts ?? []) {
        if (p.user_id) ids.add(p.user_id as string);
      }
    }
  }
  // Upward packet authors / packagers on this cycle
  const { data: proposals } = await supabase
    .from('catchball_proposals')
    .select('proposed_by, packaged_by')
    .eq('cycle_id', cycleId)
    .eq('direction', 'up');
  for (const p of proposals ?? []) {
    if (p.proposed_by) ids.add(p.proposed_by as string);
    if (p.packaged_by) ids.add(p.packaged_by as string);
  }
  return [...ids];
}

function mapRow(row: Record<string, unknown>): CycleReport {
  return {
    id: row.id as string,
    cycleId: row.cycle_id as string,
    cycleName: row.cycle_name as string | undefined,
    cycleMode: row.cycle_mode as string | undefined,
    cycleOwnerId: row.cycle_owner_id as string | undefined,
    effectiveDate: row.effective_date as string | undefined,
    timeHorizon: row.time_horizon as string | undefined,
    prioritySetId: row.priority_set_id as string | undefined,
    loopReportId: row.loop_report_id as string | undefined,
    loopResultIds: (row.loop_result_ids as string[]) ?? [],
    proposalId: row.proposal_id as string | undefined,
    rankedItemIds: (row.ranked_item_ids as string[]) ?? [],
    cutLinePosition: row.cut_line_position as number | undefined,
    itemSnapshot: (row.item_snapshot as CycleReport['itemSnapshot']) ?? [],
    legitimacyState: row.legitimacy_state as LegitimacyState,
    planOfRecord: (row.plan_of_record as PlanOfRecord) ?? ({} as PlanOfRecord),
    scoringBasis: row.scoring_basis as ScoringBasis | undefined,
    ratificationAuthority: row.ratification_authority as string | undefined,
    amendmentLog: (row.amendment_log as AmendmentLogEntry[] | undefined) ?? undefined,
    overrides: row.overrides,
    rationale: row.rationale as string | undefined,
    assumptions: row.assumptions as string | undefined,
    risks: row.risks as string | undefined,
    decisionNote: row.decision_note as string | undefined,
    decisionConfidence: row.decision_confidence as string | undefined,
    governanceEventIds: (row.governance_event_ids as string[]) ?? [],
    publicationMessage: row.publication_message as string | undefined,
    publicationUrl: row.publication_url as string | undefined,
    notifiedStakeholderIds: (row.notified_stakeholder_ids as string[]) ?? [],
    version: row.version as number,
    supersedesId: row.supersedes_id as string | undefined,
    ratifiedAt: row.ratified_at as string | undefined,
    ratifiedBy: row.ratified_by as string | undefined,
    publishedAt: row.published_at as string | undefined,
    publishedBy: row.published_by as string | undefined,
    createdBy: row.created_by as string,
    createdAt: row.created_at as string,
    workspaceId: row.workspace_id as string | undefined,
  };
}

// ============================================================================
// Store
// ============================================================================

export const useCycleReportStore = create<CycleReportStoreState>()(
  (set, get) => ({
    reports: [],
    isLoading: false,
    error: null,

    // =========================================================================
    // writeCycleReport — thin client wrapper around write_cycle_report RPC
    // The actual work happens server-side atomically (Brief G Correction §1).
    // This is also called internally by catchball_accept RPC (migration 763; originally 319).
    // Client-side call is a fallback for manual retry only.
    // =========================================================================

    writeCycleReport: async (params) => {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return null;

      const { data, error } = await supabase.rpc('write_cycle_report', {
        p_cycle_id:    params.cycleId,
        p_loop_id:     params.loopId,
        p_proposal_id: params.proposalId,
        p_actor_id:    params.actorId,
      });

      if (error) {
        console.error('[CycleReport] write_cycle_report RPC error:', error);
        return null;
      }

      const result = data as { success: boolean; report_id?: string; error?: string };
      if (!result?.success) {
        console.error('[CycleReport] write_cycle_report RPC failed:', result?.error);
        return null;
      }

      // Refresh local state
      void get().fetchCurrentCycleReport(params.cycleId).then(r => {
        if (r) {
          set(state => ({
            reports: [
              ...state.reports.filter(rep => rep.cycleId !== params.cycleId || rep.legitimacyState === 'superseded'),
              r,
            ],
          }));
        }
      });

      dispatchPrioritizationDashboardRefresh({ source: 'cycleReport:write' });

      return result.report_id ?? null;
    },

    // =========================================================================
    // publishCycleReport — calls publish_cycle_report RPC (Brief G Correction §1)
    // =========================================================================

    publishCycleReport: async (params) => {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return { success: false, error: 'Not connected to the server.' };

      // Prefer explicit stakeholder list; otherwise notify everyone who worked
      // the cycle (session facilitators + participants + packet authors).
      let stakeholderIds = [...(params.stakeholderIds ?? [])];
      const { data: reportRow } = await supabase
        .from('cycle_reports')
        .select('id, cycle_id')
        .eq('id', params.reportId)
        .maybeSingle();
      const cycleId =
        (reportRow?.cycle_id as string | undefined) ??
        get().reports.find((r) => r.id === params.reportId)?.cycleId;

      if (stakeholderIds.length === 0 && cycleId) {
        stakeholderIds = await resolveCyclePublishRecipients(supabase, cycleId);
      }

      const { data, error } = await supabase.rpc('publish_cycle_report', {
        p_report_id:           params.reportId,
        p_actor_id:            params.actorId,
        p_publication_message: params.publicationMessage,
        p_publication_url:     params.publicationUrl ?? null,
        p_stakeholder_ids:     stakeholderIds,
      });

      if (error) {
        console.error('[CycleReport] publish_cycle_report RPC error:', error);
        return { success: false, error: error.message };
      }

      const result = data as { success: boolean; error?: string; message?: string };
      if (!result?.success) {
        const message = result?.message ?? result?.error ?? 'The server rejected the publish.';
        console.error('[CycleReport] publish_cycle_report RPC failed:', message);
        return { success: false, error: message };
      }

      // Notify stakeholders the priorities are live (brief-X §5) — the RPC
      // records notified_stakeholder_ids but delivers nothing itself.
      const report = get().reports.find(r => r.id === params.reportId);
      const recipients = stakeholderIds.filter(id => id !== params.actorId);
      if (recipients.length > 0) {
        void sendNotification({
          event: 'cycle_priorities_published',
          channels: ['in_app', 'email'],
          recipientIds: recipients,
          senderId: params.actorId,
          title: 'Priorities published',
          body: params.publicationMessage || 'The ratified priorities for your cycle are now the plan.',
          priority: 'high',
          actionUrl: cycleId
            ? `/prioritization/cycles/${cycleId}/report`
            : report
              ? `/prioritization/cycles/${report.cycleId}/report`
              : '/prioritization/reports/cycles',
          actionLabel: 'See where things landed',
          data: { reportId: params.reportId, cycleId: cycleId ?? report?.cycleId },
        }).catch(err => console.warn('[CycleReport] Publish notification failed (non-blocking):', err));
      }

      // Refresh local state from DB
      const now = new Date().toISOString();
      set(state => ({
        reports: state.reports.map(rep =>
          rep.id === params.reportId
            ? {
                ...rep,
                legitimacyState: 'published',
                publishedAt: now,
                publishedBy: params.actorId,
                publicationMessage: params.publicationMessage,
                publicationUrl: params.publicationUrl,
                notifiedStakeholderIds: stakeholderIds,
              }
            : rep
        ),
      }));

      dispatchPrioritizationDashboardRefresh({ source: 'cycleReport:publish' });

      return { success: true, error: null };
    },

    // =========================================================================
    // fetchCurrentCycleReport — most recent non-superseded report
    // =========================================================================

    fetchCurrentCycleReport: async (cycleId) => {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return null;

      const { data, error } = await supabase
        .from('cycle_reports')
        .select('*')
        .eq('cycle_id', cycleId)
        .in('legitimacy_state', ['ratified', 'published'])
        .order('version', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('[CycleReport] fetchCurrentCycleReport error:', error);
        return null;
      }

      return data ? mapRow(data as Record<string, unknown>) : null;
    },

    // =========================================================================
    // fetchCycleReportHistory — full version list ascending
    // =========================================================================

    fetchCycleReportHistory: async (cycleId) => {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return [];

      const { data, error } = await supabase
        .from('cycle_reports')
        .select('id, version, legitimacy_state, ratified_at, published_at, created_by, rationale, amendment_log')
        .eq('cycle_id', cycleId)
        .order('version', { ascending: true });

      if (error) {
        console.error('[CycleReport] fetchCycleReportHistory error:', error);
        return [];
      }

      return ((data ?? []) as Array<Record<string, unknown>>).map(row => ({
        id: row.id as string,
        version: row.version as number,
        legitimacyState: row.legitimacy_state as LegitimacyState,
        ratifiedAt: row.ratified_at as string | undefined,
        publishedAt: row.published_at as string | undefined,
        createdBy: row.created_by as string,
        rationale: row.rationale as string | undefined,
        amendmentLog: (row.amendment_log as AmendmentLogEntry[] | undefined) ?? undefined,
      }));
    },

    // =========================================================================
    // fetchReportsForCycle — loads all reports into local state
    // =========================================================================

    fetchReportsForCycle: async (cycleId) => {
      if (!isSupabaseAvailable()) return;

      const supabase = getSafeSupabaseClient();
      if (!supabase) return;

      set({ isLoading: true, error: null });

      try {
        const { data, error } = await supabase
          .from('cycle_reports')
          .select('*')
          .eq('cycle_id', cycleId)
          .order('version', { ascending: false });

        if (error) {
          console.error('[CycleReportStore] Failed to fetch reports:', error);
          set({ isLoading: false, error: error.message });
          return;
        }

        const reports = ((data ?? []) as Array<Record<string, unknown>>).map(mapRow);

        set(state => {
          const otherReports = state.reports.filter(r => r.cycleId !== cycleId);
          return { reports: [...otherReports, ...reports], isLoading: false };
        });
      } catch (err) {
        console.error('[CycleReportStore] Exception fetching reports:', err);
        set({ isLoading: false, error: String(err) });
      }
    },

    // =========================================================================
    // Local selectors
    // =========================================================================

    getLatestReportForCycle: (cycleId) => {
      return get()
        .reports.filter(r => r.cycleId === cycleId)
        .sort((a, b) => b.version - a.version)[0];
    },

    getReportsForCycle: (cycleId) => {
      return get()
        .reports.filter(r => r.cycleId === cycleId)
        .sort((a, b) => b.version - a.version);
    },

    getPublishedReport: (cycleId) => {
      return get().reports.find(r => r.cycleId === cycleId && r.legitimacyState === 'published');
    },
  })
);

export default useCycleReportStore;
